/**
 * The popup manager plugin manages popups on behalf of other plugins.
 *
 * Popups are defined declaratively by the state in the popup manager plugin.
 *
 * When the view updates, the manager creates the DOM representation of the
 * popups in the view and keeps it in the right position.
 */

import { Plugin, PluginKey, EditorState, Transaction } from "prosemirror-state";
import { EditorView } from "prosemirror-view";
import { Node, Mark } from "prosemirror-model";

import {
    TAG_NAME as DYNAMIC_POPUP_TAG_NAME,
    EditorDynamicPopup,
} from "./component/popup.js";
import { expandMark } from "@/wc/text-editor/lib/helpers";

export interface PopupSpec {
    /**
     * Callback that creates a DOM element that will be rendered in the popup.
     * When nothing is returned, the whole popup will not be rendered.
     *
     * @param view Editor view to which the popup will be attached. Can be used to
     * access the editor state when the popup DOM is constructed.
     *
     * @returns The popup contents or undefined.
     */
    create: (
        view: EditorView
    ) => Promise<Element | undefined> | Element | undefined;
    /**
     * Should only ever be set by the popup manager. Allows async creation of the popup.
     */
    _creating?: boolean;
    /**
     * Start position in the document where the popup will be anchored.
     */
    startPos?: number;
    /**
     * End position in the document where the popup will be anchored.
     */
    endPos?: number;
    /**
     * Popups can be attached to a specific document node and observe changes to
     * that node.
     */
    node?: Node;
    /**
     * Whether to close on a mouseleave event.
     */
    closeOnMouseLeave?: boolean;
    /**
     * Disabled on editable is false.
     */
    onlyInEditMode?: boolean;
    /**
     * Disabled on editable is true.
     */
    onlyInViewMode?: boolean;
}

/**
 * A handler generates the proper popupspec. This can be injected from the outside
 * so that the hover functionality is centrally managed here, but the popups can be
 * customized by the user.
 */
export type PopupHandler = (
    node: Node,
    cursor: { pos: number; inside: number; startPos: number; endPos: number },
    mark?: Mark
) => PopupSpec;

export interface PopupManagerState {
    popups: { [key: string]: PopupSpec };
    handlers: { [key: string]: PopupHandler };
}

export const POPUP_MANAGER_PLUGIN = new PluginKey<PopupManagerState>(
    "POPUP_MANAGER_PLUGIN"
);

/**
 * Create and remove popups when requested.
 */
export const popupManagerPlugin = () => {
    const popupHost = document.createElement("div");
    popupHost.className = "popupHost";
    popupHost.style.position = "absolute";
    popupHost.style.zIndex = "1";
    popupHost.style.width = "100%";
    popupHost.style.height = "0";

    return new Plugin<PopupManagerState>({
        popupHost,
        key: POPUP_MANAGER_PLUGIN,
        state: {
            init() {
                return {
                    popups: {},
                    handlers: {},
                };
            },
            apply(tr, value) {
                const mappedPopups = Object.fromEntries(
                    Object.entries(value.popups)
                        .map(([key, spec]) => {
                            if (!spec) {
                                return;
                            }
                            const newStartPos = spec?.startPos
                                ? tr.mapping.map(spec.startPos)
                                : null;
                            const newEndPos = spec?.endPos
                                ? tr.mapping.map(spec.endPos)
                                : null;

                            if (
                                (spec.startPos && !newStartPos) ||
                                (spec.endPos && !newEndPos)
                            ) {
                                // The content this popup was anchored to does no longer exist after the transaction
                                // so remove the popup.
                                return;
                            }
                            return [
                                key,
                                {
                                    ...spec,
                                    startPos: newStartPos,
                                    endPos: newEndPos,
                                },
                            ] as [string, PopupSpec];
                        })
                        .filter((v) => v !== undefined)
                );

                if (tr.getMeta(POPUP_MANAGER_PLUGIN)) {
                    return {
                        ...value,
                        popups: {
                            ...mappedPopups,
                            ...tr.getMeta(POPUP_MANAGER_PLUGIN),
                        },
                    };
                }
                return {
                    ...value,
                    popups: mappedPopups,
                };
            },
        },
        /**
         * On view update, sync the popup specs in the plugin state to the view.
         */
        view(view) {
            const popupsContainer =
                view.dom.ownerDocument.querySelector("#popups-container") ||
                view.dom.parentNode;
            popupsContainer!.appendChild(popupHost);
            return {
                update(view) {
                    const managerState = POPUP_MANAGER_PLUGIN.getState(
                        view.state
                    );
                    if (!managerState) return;

                    for (const [key, spec] of Object.entries(
                        managerState.popups
                    )) {
                        const domNode = getPopupView(view.state, key);
                        const correctMode =
                            spec &&
                            (spec.onlyInEditMode === view.editable ||
                                spec.onlyInViewMode === !view.editable);
                        if (domNode && !spec) {
                            // Popup closed in plugin state but still exists in the DOM
                            popupHost.removeChild(domNode);
                        } else if (
                            !domNode &&
                            spec &&
                            correctMode &&
                            !spec._creating
                        ) {
                            // Popup opened in plugin state but not yet created in the DOM
                            spec._creating = true;
                            createDynamicPopup(view, spec, key).then(
                                (popupWrapper) => {
                                    if (popupWrapper) {
                                        popupHost.appendChild(popupWrapper);
                                    }
                                    spec._creating = false;
                                }
                            );
                        } else if (domNode && spec) {
                            if (!correctMode) {
                                popupHost.removeChild(domNode);
                            }
                            // Plugin with this spec was already open. Sync the spec.
                            if (spec.startPos) {
                                domNode.setAttribute(
                                    "data-document-start-pos",
                                    spec.startPos.toString()
                                );
                            }
                            if (spec.endPos) {
                                domNode.setAttribute(
                                    "data-document-end-pos",
                                    spec.endPos.toString()
                                );
                            }
                        }
                    }
                    // HACK: Somehow the `managerState` is empty when hovering
                    // with popups in readonly mode.
                    Array.from(popupHost.children).forEach((child: Element) => {
                        // Remove left over popups.
                        if (
                            Object.keys(managerState.popups).includes(
                                (child as HTMLElement).dataset.popupKey || ""
                            )
                        ) {
                            return;
                        }
                        popupHost.removeChild(child);
                    });
                },
                destroy() {
                    view.dom.parentNode?.removeChild(popupHost);
                },
            };
        },

        props: {
            handleDOMEvents: {
                dragstart: (view) => {
                    // Close all popups, regardless of their settings.
                    view.dispatch(closeAllPopups(view.state.tr, view.state));
                },
                mousemove: (view, event) => {
                    if (!(event.target instanceof Element)) {
                        return;
                    }

                    const managerState = POPUP_MANAGER_PLUGIN.getState(
                        view.state
                    );
                    if (!managerState) return;

                    // Get theoretical position of cursor in document based on mouse position.
                    const cursor = view.posAtCoords({
                        left: event.clientX,
                        top: event.clientY,
                    });

                    if (!cursor || cursor.inside < 0) {
                        return;
                    }

                    // Get node for given cursor position.
                    const node = view.state.doc.nodeAt(cursor.inside);
                    if (!node) {
                        return;
                    }

                    let transaction: Transaction | undefined;

                    // If any popup is open...
                    if (
                        Object.keys(managerState.popups).some((key) => {
                            return getPopupView(view.state, key);
                        })
                    ) {
                        // Close all in transaction, since it will be reopened anyway.
                        transaction = closeAllPopups(
                            view.state.tr,
                            view.state,
                            (spec) => !spec || spec.closeOnMouseLeave || false
                        );
                    }

                    // TODO: Explicitly set a PopupMark which is the ONLY way to trigger the popup
                    const marks = view.state.doc.resolve(cursor.pos).marks();
                    const popupMark = marks.find((mark) => mark.attrs.popupKey);
                    const popupKey = popupMark
                        ? popupMark.attrs.popupKey
                        : node.attrs.popupKey;
                    const popupHover = popupMark
                        ? popupMark.attrs.popupHover
                        : node.attrs.popupHover;

                    if (popupKey && popupHover) {
                        const spec = getPopupSpec(view.state, popupKey);

                        // For marks, find the exact mark boundaries
                        let startPos = cursor.pos;
                        let endPos = cursor.pos;

                        if (popupMark) {
                            const range = expandMark(
                                view.state.doc,
                                cursor.pos,
                                popupMark
                            );
                            startPos = range.from;
                            endPos = range.to;
                        } else {
                            // Fallback to node boundaries if no mark
                            startPos = cursor.inside;
                            endPos = cursor.inside + node.nodeSize;
                        }

                        if (
                            spec?.startPos === startPos &&
                            spec?.endPos === endPos
                        ) {
                            // Don't open the popup if it's for the same range
                            return;
                        }

                        // Remove the old popup sync so we're guaranteed it is recreated.
                        const domNode = getPopupView(view.state, popupKey);
                        domNode?.remove();

                        // Hovering over something with a popup, so quick, show it!
                        const handler = getPopupHandler(view.state, popupKey);
                        transaction = addPopup(
                            transaction ?? view.state.tr,
                            popupKey,
                            {
                                node: node,
                                startPos,
                                endPos,
                                ...handler(
                                    node,
                                    { ...cursor, startPos, endPos },
                                    popupMark
                                ),
                            }
                        );
                    }

                    if (transaction) {
                        return view.dispatch(transaction);
                    }
                },
                mouseleave: (view, event) => {
                    if (!(event.relatedTarget instanceof Element)) {
                        return;
                    }

                    // Don't close when you move to hovering over the popup itself.
                    if (
                        event.relatedTarget.tagName.toLowerCase() ===
                        DYNAMIC_POPUP_TAG_NAME
                    ) {
                        return;
                    }
                    view.dispatch(
                        closeAllPopups(
                            view.state.tr,
                            view.state,
                            (spec) => !spec || spec.closeOnMouseLeave || false
                        )
                    );
                },
            },
        },
    });
};

/**
 * Modify a transaction to request adding a popup. The popup will be added to the view by
 * the popup manager plugin.
 */
export const addPopup = (
    tr: Transaction,
    key: string,
    popup: PopupSpec
): Transaction => {
    return tr.setMeta(POPUP_MANAGER_PLUGIN, {
        ...(tr.getMeta(POPUP_MANAGER_PLUGIN) ?? {}),
        [key]: popup,
    });
};

/**
 * Close all popups, optionally with a filter.
 */
export const closeAllPopups = (
    tr: Transaction,
    state: EditorState,
    filter: (spec: PopupSpec) => boolean = () => true
) => {
    const popupSpecs = getPopupSpecs(state);
    let transaction = tr;

    for (const [key, spec] of Object.entries(popupSpecs)) {
        if (filter(spec)) {
            transaction = closePopup(transaction, key);
        }
    }
    return transaction;
};

/**
 * Close the popup with a given key.
 */
export const closePopup = (tr: Transaction, key: string): Transaction => {
    return tr.setMeta(POPUP_MANAGER_PLUGIN, {
        ...(tr.getMeta(POPUP_MANAGER_PLUGIN) ?? {}),
        [key]: null,
    });
};

/**
 * Get an object with all popupSpecs.
 */
export const getPopupSpecs = (
    state: EditorState
): Record<string, PopupSpec> => {
    const managerState = POPUP_MANAGER_PLUGIN.getState(state);
    return managerState?.popups ?? {};
};

/**
 * Get the popup spec with the given key in the specified state.
 */
export const getPopupSpec = (
    state: EditorState,
    key: string
): PopupSpec | null => {
    return getPopupSpecs(state)?.[key];
};

/**
 * Get a reference to an open popup if it still exists
 */
export const getPopupView = <T extends EditorDynamicPopup>(
    state: EditorState,
    key: string
): T | null => {
    const popupHost = POPUP_MANAGER_PLUGIN.get(state)?.spec
        .popupHost as HTMLElement;
    return popupHost.querySelector<T>(`[data-popup-key=${key}]`);
};

/**
 * Get the contents of the popup.
 */
export const getPopupContents = <T extends HTMLElement>(
    state: EditorState,
    key: string
): T | null => {
    return getPopupView(state, key)
        ?.shadowRoot?.querySelector<HTMLSlotElement>("slot[name='contents']")
        ?.assignedElements()[0] as T | null;
};

/**
 * Get registered handlers for all popup keys.
 */
const getPopupHandlers = (state: EditorState): Record<string, PopupHandler> => {
    const managerState = POPUP_MANAGER_PLUGIN.getState(state);
    return managerState?.handlers ?? {};
};

/**
 * Get handler for specific popup key.
 */
const getPopupHandler = (state: EditorState, key: string): PopupHandler => {
    return getPopupHandlers(state)[key];
};

// Helper to wrap an element in an inline popup
const createDynamicPopup = async (
    view: EditorView,
    spec: PopupSpec,
    key: string
): Promise<EditorDynamicPopup | undefined> => {
    const element = await spec.create(view);
    if (!element) return;

    const wrapper = new EditorDynamicPopup({
        pos: {
            start: spec.startPos ?? 0,
            end: spec?.endPos ?? spec.startPos ?? 0,
        },
        key,
        coordsAtDocumentPosition: (pos) => view.coordsAtPos(pos),
        close: () => view.dispatch(closePopup(view.state.tr, key)),
        closeOnMouseLeave: spec.closeOnMouseLeave,
    });
    wrapper.dataset.key = key;

    // Set the contents using a slot. This allows custom / general styling to apply.
    element.slot = "contents";
    wrapper.appendChild(element);

    return wrapper;
};
